import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, CreditCard, CheckCircle, XCircle, Key, EnvelopeSimple } from "@phosphor-icons/react";

const ROLES = ["employee", "manager", "hr", "admin"];

const empty = {
  email: "", name: "", role: "employee", team: "", nfc_uid: "", employee_code: "", manager_id: "",
  vacation_balance_days: 20, vacation_balance_prior_year: 0, daily_hours_obligation: 8.0,
  active: true, password: "",
};

export default function Employees() {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/users", { params: q ? { q } : {} });
      setList(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email, name: u.name, role: u.role, team: u.team || "",
      nfc_uid: u.nfc_uid || "", employee_code: u.employee_code || "", manager_id: u.manager_id || "",
      vacation_balance_days: u.vacation_balance_days ?? 20,
      vacation_balance_prior_year: u.vacation_balance_prior_year ?? 0,
      daily_hours_obligation: u.daily_hours_obligation ?? 8,
      active: u.active, password: "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        delete payload.email;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/users", form);
        toast.success("Created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete user?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetByEmail = async (u) => {
    if (!window.confirm(t("emp_reset_email_confirm") + "\n\n" + u.email)) return;
    try {
      const { data } = await api.post("/auth/admin_reset_password", { user_id: u.id, mode: "email" });
      if (data.email_sent) toast.success(t("emp_reset_email_sent") + " · " + u.email);
      else toast.success(t("emp_reset_link_generated"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetByTemp = async (u) => {
    if (!window.confirm(t("emp_reset_temp_confirm") + "\n\n" + u.email)) return;
    try {
      const { data } = await api.post("/auth/admin_reset_password", { user_id: u.id, mode: "temp" });
      window.prompt(`${t("emp_temp_password_title")} — ${u.name}\n\n${t("emp_temp_password_hint")}`, data.temp_password);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const managers = list.filter((u) => ["manager", "hr", "admin"].includes(u.role));

  // Enrollment state
  const [enroll, setEnroll] = useState(null); // { user, session, secondsLeft }
  const enrollTimerRef = useRef(null);
  const pollRef = useRef(null);

  const startEnroll = async (u) => {
    try {
      const { data: session } = await api.post(`/users/${u.id}/enroll/start`);
      setEnroll({ user: u, session, secondsLeft: 120, status: "pending" });
      // start polling
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const { data: s } = await api.get(`/users/${u.id}/enroll/status`);
          setEnroll((prev) => prev ? { ...prev, session: s, status: s.status } : prev);
          if (s.status === "completed") {
            clearInterval(pollRef.current);
            clearInterval(enrollTimerRef.current);
            toast.success(`Card linked to ${u.name}`);
            load();
          } else if (s.status === "expired" || s.status === "cancelled") {
            clearInterval(pollRef.current);
            clearInterval(enrollTimerRef.current);
          }
        } catch {}
      }, 1500);
      if (enrollTimerRef.current) clearInterval(enrollTimerRef.current);
      enrollTimerRef.current = setInterval(() => {
        setEnroll((prev) => {
          if (!prev || prev.status !== "pending") return prev;
          const n = prev.secondsLeft - 1;
          return { ...prev, secondsLeft: Math.max(0, n) };
        });
      }, 1000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancelEnroll = async () => {
    if (!enroll) return;
    try { await api.post(`/users/${enroll.user.id}/enroll/cancel`); } catch {}
    if (pollRef.current) clearInterval(pollRef.current);
    if (enrollTimerRef.current) clearInterval(enrollTimerRef.current);
    setEnroll(null);
  };

  const closeEnroll = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (enrollTimerRef.current) clearInterval(enrollTimerRef.current);
    setEnroll(null);
  };

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (enrollTimerRef.current) clearInterval(enrollTimerRef.current);
  }, []);

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-black/60">{t("employees")}</div>
          <h1 className="font-display font-black text-5xl tracking-tighter leading-none">Employees & badges</h1>
        </div>
        <div className="flex gap-2">
          <input
            data-testid="employees-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            className="brut-border p-2 px-3"
          />
          <button
            data-testid="add-employee-btn"
            onClick={openCreate}
            className="bg-black text-white brut-border brut-shadow brut-hover px-4 py-2 font-bold uppercase tracking-wider text-xs flex items-center gap-2"
          >
            <Plus size={16} weight="bold" /> {t("add_employee")}
          </button>
        </div>
      </div>

      <div className="brut-border bg-white brut-shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F4F0] uppercase tracking-wider text-xs">
              <tr>
                <th className="text-left p-3 border-b-2 border-black">{t("name")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("role")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("team")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("badge")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("vacation_current_year")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("vacation_prior_year")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("active")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("action")}</th>
              </tr>
            </thead>
            <tbody data-testid="employees-table">
              {list.map((u) => (
                <tr key={u.id} className="border-b border-black/10">
                  <td className="p-3">
                    <div className="font-bold">{u.name}</div>
                    <div className="text-xs font-mono text-black/60">{u.email}</div>
                  </td>
                  <td className="p-3"><span className="tag bg-[#FFCC00]">{t(u.role)}</span></td>
                  <td className="p-3">{u.team || "—"}</td>
                  <td className="p-3 font-mono text-xs">{u.nfc_uid || "—"}</td>
                  <td className="p-3 font-bold">{u.vacation_balance_days}</td>
                  <td className="p-3 font-mono">{u.vacation_balance_prior_year ?? 0}</td>
                  <td className="p-3">{u.active ? "✓" : "✗"}</td>
                  <td className="p-3">
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => startEnroll(u)} data-testid={`enroll-${u.id}`} title="Enroll card" className="brut-border px-2 py-1 bg-[#FFCC00] hover:bg-black hover:text-white"><CreditCard size={14} weight="bold" /></button>
                      <button onClick={() => resetByEmail(u)} data-testid={`reset-email-${u.id}`} title={t("emp_reset_email_tip")} className="brut-border px-2 py-1 bg-[#0047FF] text-white hover:bg-black"><EnvelopeSimple size={14} weight="bold" /></button>
                      <button onClick={() => resetByTemp(u)} data-testid={`reset-temp-${u.id}`} title={t("emp_reset_temp_tip")} className="brut-border px-2 py-1 bg-[#FF8000] text-black hover:bg-black hover:text-white"><Key size={14} weight="bold" /></button>
                      <button onClick={() => openEdit(u)} data-testid={`edit-${u.id}`} className="brut-border px-2 py-1 bg-white hover:bg-[#FFCC00]"><PencilSimple size={14} weight="bold" /></button>
                      <button onClick={() => remove(u.id)} data-testid={`delete-${u.id}`} className="brut-border px-2 py-1 bg-white hover:bg-[#FF3333] hover:text-white"><Trash size={14} weight="bold" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-black/60">{t("no_data")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setOpen(false)}>
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            data-testid="employee-dialog"
            className="brut-border bg-white p-6 brut-shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-black text-2xl tracking-tighter">{editing ? "Edit employee" : t("add_employee")}</div>
              <button type="button" onClick={() => setOpen(false)}><X size={20} weight="bold" /></button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label={t("email")}><input data-testid="emp-email" type="email" required disabled={!!editing} value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="w-full brut-border p-2 disabled:opacity-60" /></Field>
              <Field label={t("name")}><input data-testid="emp-name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full brut-border p-2" /></Field>
              <Field label={t("role")}>
                <select data-testid="emp-role" value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} className="w-full brut-border p-2 bg-white">
                  {ROLES.map(r => <option key={r} value={r}>{t(r)}</option>)}
                </select>
              </Field>
              <Field label={t("team")}><input data-testid="emp-team" value={form.team} onChange={(e) => setForm({...form, team: e.target.value})} className="w-full brut-border p-2" /></Field>
              <Field label={t("badge")}><input data-testid="emp-nfc" value={form.nfc_uid} onChange={(e) => setForm({...form, nfc_uid: e.target.value})} className="w-full brut-border p-2 font-mono" /></Field>
              <Field label="Payroll code (Šifra del.)"><input data-testid="emp-code" placeholder="e.g. 000003" value={form.employee_code} onChange={(e) => setForm({...form, employee_code: e.target.value})} className="w-full brut-border p-2 font-mono" /></Field>
              <Field label="Manager">
                <select data-testid="emp-manager" value={form.manager_id} onChange={(e) => setForm({...form, manager_id: e.target.value})} className="w-full brut-border p-2 bg-white">
                  <option value="">— none —</option>
                  {managers.map(m => <option key={m.id} value={m.id}>{m.name} ({t(m.role)})</option>)}
                </select>
              </Field>
              <Field label={t("vacation_current_year")}><input type="number" step="0.5" data-testid="emp-balance" value={form.vacation_balance_days} onChange={(e) => setForm({...form, vacation_balance_days: Number(e.target.value)})} className="w-full brut-border p-2" /></Field>
              <Field label={t("vacation_prior_year")}><input type="number" step="0.5" data-testid="emp-balance-prior" value={form.vacation_balance_prior_year} onChange={(e) => setForm({...form, vacation_balance_prior_year: Number(e.target.value)})} className="w-full brut-border p-2" /></Field>
              <Field label="Daily hours obligation"><input type="number" step="0.5" data-testid="emp-obligation" value={form.daily_hours_obligation} onChange={(e) => setForm({...form, daily_hours_obligation: Number(e.target.value)})} className="w-full brut-border p-2" /></Field>
              <Field label={t("active")}>
                <label className="flex items-center gap-2 h-full brut-border p-2">
                  <input type="checkbox" data-testid="emp-active" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})} />
                  <span>{form.active ? t("active") : "Disabled"}</span>
                </label>
              </Field>
              <Field label={editing ? `${t("password")} (leave blank to keep)` : t("password")}>
                <input data-testid="emp-password" type="password" required={!editing} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="w-full brut-border p-2" />
              </Field>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="emp-submit"
              className="mt-6 w-full bg-black text-white brut-border brut-shadow brut-hover p-3 font-bold uppercase tracking-wider disabled:opacity-60"
            >
              {loading ? "…" : t("save")}
            </button>
          </form>
        </div>
      )}

      {enroll && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={closeEnroll}>
          <div
            data-testid="enroll-dialog"
            onClick={(e) => e.stopPropagation()}
            className="brut-border bg-white p-8 brut-shadow-lg max-w-lg w-full"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CreditCard size={28} weight="bold" />
                <div className="font-display font-black text-2xl tracking-tighter">{t("enroll_card")}</div>
              </div>
              <button onClick={closeEnroll}><X size={20} weight="bold" /></button>
            </div>

            <div className="brut-border bg-[#F4F4F0] p-4 mb-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-black/60">{t("employee")}</div>
              <div className="font-display font-black text-2xl tracking-tighter">{enroll.user.name}</div>
              <div className="text-xs font-mono">{enroll.user.email}</div>
              {enroll.user.nfc_uid && (
                <div className="mt-1 text-xs font-mono text-black/60">
                  {t("current_card")}: <span className="font-bold">{enroll.user.nfc_uid}</span>
                </div>
              )}
            </div>

            {enroll.status === "pending" && (
              <div className="brut-border bg-[#FFCC00] p-5 text-center">
                <div className="mx-auto w-20 h-20 brut-border bg-[#0047FF] pulse-ring mb-4" />
                <div className="font-display font-black text-3xl tracking-tighter leading-none mb-2">
                  {t("tap_new_card")}
                </div>
                <div className="text-xs font-bold uppercase tracking-wider">
                  {t("expires_in")}: <span className="font-mono text-lg">
                    {Math.floor(enroll.secondsLeft / 60)}:{String(enroll.secondsLeft % 60).padStart(2, "0")}
                  </span>
                </div>
              </div>
            )}

            {enroll.status === "completed" && (
              <div className="brut-border bg-[#00CC33] p-5 text-center">
                <CheckCircle size={48} weight="fill" className="mx-auto mb-2" />
                <div className="font-display font-black text-3xl tracking-tighter leading-none">{t("card_linked")}</div>
                <div className="mt-2 text-xs font-mono">
                  UID: <span className="font-bold">{enroll.session?.nfc_uid}</span>
                </div>
              </div>
            )}

            {(enroll.status === "expired" || enroll.status === "cancelled") && (
              <div className="brut-border bg-[#FF3333] text-white p-5 text-center">
                <XCircle size={48} weight="fill" className="mx-auto mb-2" />
                <div className="font-display font-black text-2xl tracking-tighter leading-none uppercase">
                  {enroll.status === "expired" ? t("enroll_expired") : t("enroll_cancelled")}
                </div>
              </div>
            )}

            <div className="mt-4 flex gap-2">
              {enroll.status === "pending" && (
                <button
                  data-testid="enroll-cancel"
                  onClick={cancelEnroll}
                  className="flex-1 brut-border bg-white brut-shadow brut-hover px-4 py-2 font-bold uppercase tracking-wider text-xs"
                >
                  {t("cancel")}
                </button>
              )}
              {enroll.status !== "pending" && (
                <button
                  data-testid="enroll-close"
                  onClick={closeEnroll}
                  className="flex-1 bg-black text-white brut-border brut-shadow brut-hover px-4 py-2 font-bold uppercase tracking-wider text-xs"
                >
                  OK
                </button>
              )}
              {(enroll.status === "expired" || enroll.status === "cancelled") && (
                <button
                  data-testid="enroll-retry"
                  onClick={() => startEnroll(enroll.user)}
                  className="flex-1 bg-[#0047FF] text-white brut-border brut-shadow brut-hover px-4 py-2 font-bold uppercase tracking-wider text-xs"
                >
                  {t("retry")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider mb-1">{label}</label>
      {children}
    </div>
  );
}
